import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import { Moment } from 'moment'
import { isUndefined } from 'lodash-es'
import { getFormattedTime, getTimeLeft } from 'utils'
import { iconSet } from '../icons'
import * as constants from 'view/styles/constants'
import * as mixins from 'view/styles/mixins'
import { IntervalWrapper } from './IntervalWrapper'
import { serverClientTimeOffsetSelector } from 'selectors/timeSelectors'
import { timeConstants } from './timeConstants'

export interface CountdownTimerProps {
  expiryTime: Moment
  hideIcon?: boolean
  serverClientTimeOffset: number
  label?: string
  noTick?: boolean
  onComplete?: () => unknown
  expiringTime?: number
  urgentTime?: number
  dataTestId?: string
}

const mapStateToProps = createStructuredSelector({
  serverClientTimeOffset: serverClientTimeOffsetSelector
})

const TimerPart = styled.span<{ trivial?: boolean }>`
  font-weight: ${props => (props.trivial ? constants.FontWeight.Normal : constants.FontWeight.Bold)};
  line-height: 1.1em;
  display: flex;
  align-items: center;
`

const TimeLeft = styled.span`
  display: flex;
  font-variant-numeric: tabular-nums;
  padding: 0 5px;
`

const GridContainer = styled.div<{ timerColor: string }>`
  ${mixins.flexCenterContentCrossAxis};
  justify-content: flex-end;
  width: 100%;
  height: 100%;
  padding: 0 10px;
  font-size: 12px;
  color: #fff;

  ${TimeLeft} {
    background-color: ${props => (props.timerColor === 'transparent' ? constants.ACCENT_PRIMARY_2 : props.timerColor)};
    border-radius: 10px;
    height: 19px;
  }
`

const LabelContainer = styled.div<{ timerColor: string }>`
  ${mixins.flexCenterContentCrossAxis};
  font-size: 13px;
  color: ${props => (props.timerColor === 'transparent' ? constants.CORE_PRIMARY_6 : props.timerColor)};

  & > span {
    ${mixins.flexCenterContentCrossAxis};
    margin-right: 3px;
  }
  & > span:empty,
  & > span:last-of-type {
    margin-right: 0;
  }
`

export class CountdownTimerComponent extends React.Component<CountdownTimerProps> {
  render() {
    return this.props.noTick ? this.renderTimer() : <IntervalWrapper render={this.renderTimer} />
  }

  renderTimer = () => {
    const { expiryTime, dataTestId, serverClientTimeOffset, label, onComplete, urgentTime, expiringTime, hideIcon } = this.props
    const timeLeft = getTimeLeft(expiryTime.clone().add(serverClientTimeOffset, 'ms'))

    const timerColor =
      timeLeft < (urgentTime || timeConstants.defaultUrgent)
        ? constants.STATUS_COLOR_INVALID
        : timeLeft < (expiringTime || timeConstants.defaultExpiring)
        ? constants.STATUS_COLOR_EXPIRING
        : 'transparent'
    const [hours, minutes, seconds] = getFormattedTime(timeLeft).split(':')
    const Container = isUndefined(label) ? GridContainer : LabelContainer

    if (timeLeft === 0 && onComplete) {
      onComplete()
    }

    return (
      <Container data-test-id={dataTestId} role="timer" aria-labelledby="CountdownTimer-Label" timerColor={timerColor}>
        {!isUndefined(label) && (
          <TimerPart id="CountdownTimer-Label" trivial={!['OTW', 'RFQ'].includes(label) || timeLeft === 0}>
            {label}
          </TimerPart>
        )}
        <TimeLeft>
          <TimerPart trivial={timeLeft < 3600000}>{hours}:</TimerPart>
          <TimerPart trivial={timeLeft < 60000} data-test-id="timer-minutes">
            {minutes}:
          </TimerPart>
          <TimerPart trivial={timeLeft === 0}>{seconds}</TimerPart>
        </TimeLeft>
        {hideIcon ||
          (!isUndefined(label) && (
            <TimerPart data-test-id="CountdownTimer-TimerIcon" trivial={timeLeft === 0}>
              {iconSet.timerIcon}
            </TimerPart>
          ))}
      </Container>
    )
  }
}

export const CountdownTimer = connect(mapStateToProps)(CountdownTimerComponent)
